// @flow

import get from 'lodash/get';

import {
  Gift,
  House,
  KeyRound,
  LifeBuoy,
  LogOut,
  UserRound,
} from 'lucide-react';

import {
  createSelector,
  createStructuredSelector,
  type OutputSelector,
} from 'reselect';
import { superpathjoin } from 'superpathjoin';

import {
  selectIsAuthenticatedUser,
  selectAuthenticatedUserInfos,
  type User,
} from '@gandi/authentication';
import { selectFromConfig } from '@gandi/redux-config';
import { selectCurrentId } from '@gandi/redux-sharing';
import { localeSelector, type TranslatorProps } from '@gandi/translate';

import type { NavigationItems } from '../../../Layout/types';
import type { FullState } from '../../../types';

export type URL = string;

type Locale = 'fr' | 'en' | 'es' | 'zh' | 'zh-hant' | 'zh-hans' | 'ja' | 'de';

type ItemMap = $ReadOnly<{
  login?: string,
  user_settings: string,
  account: string,
  admin: string,
  refer_a_friend: string,
  helpdesk: string,
  logout: string,
}>;
type AllItemsMap = $ReadOnly<{
  [label: string]: string,
  ...Required<ItemMap>,
}>;
type ItemKey = $Keys<ItemMap>;

const authenticatedItems: $ReadOnlyArray<ItemKey> = [
  'admin',
  'user_settings',
  'logout',
];

const anonymousItems: $ReadOnlyArray<ItemKey> = ['login'];

function getZdLocale(locale: Locale) {
  switch (locale) {
    case 'zh-hans':
    case 'zh-hant':
      return 'zh-tw';
    case 'en':
      return 'en-us';
    default:
      return locale;
  }
}

function urlSignin(urlWithLocale: string) {
  return `/signin?return_to=${encodeURIComponent(urlWithLocale)}`;
}

const mapNavigationTranslations = (
  username: string,
  __: TranslatorProps['__'],
): AllItemsMap => ({
  admin: __('Dashboard'),
  user_settings: __('User settings'),
  account: __('Account'),
  refer_a_friend: __('Refer a friend'),
  login: __('Log in'),
  logout: __('Log out'),
  helpdesk: __('Help Center'),
  public_site: __('Public site'),
  news: __('News'),
});
const mapNavigationIcons: () => ItemMap = () => ({
  admin: House,
  account: KeyRound,
  user_settings: UserRound,
  refer_a_friend: Gift,
  logout: LogOut,
  helpdesk: LifeBuoy,
  login: undefined,
});

/** Current hostname (shop.gandi.net, admin.local.gandi.net, ...) */
const selectPublicURL = selectFromConfig<string>('public_url', '');

export type LayoutUrls = {
  [key: string]: string,
};

// XXX: rename with a meaningful name, e.g. `selectLayoutUrls`
const selectURLs = selectFromConfig<LayoutUrls>('app.layout.urls', {});

// XXX: reselect is not usefull here
const selectUsername: OutputSelector<FullState, any, string> = createSelector<
  FullState,
  empty,
  string,
  ?User,
>([selectAuthenticatedUserInfos], (userInfos: ?User) =>
  get(userInfos, 'username', ''),
);

type NavigationItemProps = {
  basename: string,
  pathname: string,
  search: string,
  __: TranslatorProps['__'],
};
/**
 * @deprecated: XXX: change this for helper , it's not a selector and react-router `basename` is not easily available anymore
 */
const selectNavigationItemsProps = createStructuredSelector({
  basename: (_: any, { basename }: NavigationItemProps) => basename,
  pathname: (_: any, { pathname }: NavigationItemProps) => pathname,
  search: (_: any, { search }: NavigationItemProps) => search,
  __: (_: any, { __ }: NavigationItemProps) => __,
});

/**
 * Select the navigation items for the user menu.
 */
const getUserItems = (
  items: $ReadOnlyArray<ItemKey>,
  publicURL: string,
  URLs: { +[string]: string, ... },
  currentId: string,
  username: string,
  locale: Locale,
  isAuthenticated: boolean,
  rafEnabled: boolean,
  basename?: string,
  pathname?: string,
  search?: string,
  __: TranslatorProps['__'],
) => {
  const translations = mapNavigationTranslations(username, __);
  const icons = mapNavigationIcons();

  return items
    .map((item: ItemKey) => {
      let href = URLs[item];

      if (item === 'login') {
        const redirectUrl = superpathjoin(
          publicURL,
          basename,
          pathname,
          search,
        );
        href = `${href}?locale=${locale}&redirect=${encodeURIComponent(
          redirectUrl,
        )}`;
      }

      if (item === 'account') {
        href = `${href}/${locale}`;
      }

      if (item === 'helpdesk') {
        href += `/${getZdLocale(locale)}`;

        if (isAuthenticated) {
          href += urlSignin(href);
        }
      }

      return {
        title: translations[item],
        href,
        type: 'main',
        icon: icons[item],
      };
    })
    .filter(Boolean);
};

export const selectUserItems: OutputSelector<
  FullState,
  NavigationItemProps,
  NavigationItems,
  // $FlowFixMe[incompatible-call]
> = createSelector(
  [
    selectPublicURL,
    selectURLs,
    selectCurrentId,
    selectUsername,
    localeSelector,
    selectIsAuthenticatedUser,
    selectFromConfig<boolean>('flags.SHOW_REFER_A_FRIEND', false),
    // XXX: to refactor
    selectNavigationItemsProps,
  ],
  (
    publicURL,
    URLs,
    currentId,
    username,
    locale,
    isAuthenticated,
    rafEnabled,
    { basename, pathname, search, __ },
  ) => {
    const items = isAuthenticated ? authenticatedItems : anonymousItems;
    return getUserItems(
      items,
      publicURL,
      URLs,
      currentId,
      username,
      locale,
      isAuthenticated,
      rafEnabled,
      basename,
      pathname,
      search,
      __,
    );
  },
);

export const selectIsComingFromPrefetch = (state: FullState): boolean =>
  !!state.app.isComingFromPrefetch;

export const selectIsAppWorking = (state: FullState): boolean =>
  !!state.app.isWorking;

export const selectIsPatchingDomainContacts = (state: FullState): boolean =>
  !!state.app.isPatchingDomainContacts;

export const selectIsPostingCheckout = (state: FullState): boolean =>
  state.app.isCheckoutSequenceLoading;
